import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

import { DialogTrigger } from 'react-aria-components';

import { Button, Popup,Stack, Text } from '@ekhodealer/ekho-common/rev';

export interface CategoryRow {
	icon: React.ReactNode;
	iconColor: string;
	count: number;
	message: string;
}

export interface ActionConfirmationModalProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	/** Array of category rows to display (e.g., ready, warning, blocked items) */
	categories: CategoryRow[];
	totalSelected: number;
	/** Number of items ready to proceed with the action */
	readyCount: number;
	onConfirm: () => void;
	onCancel: () => void;
	/** Number of items action was initiated for (0 means not yet confirmed) */
	actionInitiatedCount: number;
	/** Whether the confirm action is currently pending */
	isConfirming?: boolean;
	/** Title shown in confirmation view */
	title?: string;
	/** Subtitle template - use {count} and {plural} placeholders */
	subtitle?: string;
	/** Title shown after action is initiated */
	successTitle?: string;
	/** Icon shown in success state */
	successIcon?: React.ReactNode;
	/** Message shown after action is initiated - use {count} and {plural} placeholders */
	successMessage?: string;
	/** Secondary message shown after action is initiated */
	successDescription?: string;
	/** Text for the confirm button - use {count} placeholder */
	confirmButtonText?: string;
	/** Message shown when no items can proceed */
	noItemsMessage?: string;
}

/**
 * Status row component for displaying action categories
 */
const StatusRow = ({
	icon,
	iconColor,
	count,
	message,
}: {
	icon: React.ReactNode;
	iconColor: string;
	count: number;
	message: string;
}) => (
	<div
		style={{
			display: 'flex',
			alignItems: 'center',
			gap: '12px',
			padding: '16px',
			backgroundColor: 'var(--color-background-secondary, #f5f5f5)',
			borderRadius: '8px',
		}}>
		<div style={{ color: iconColor, display: 'flex', alignItems: 'center' }}>{icon}</div>
		<Text size='body'>
			<strong>{count}</strong> {message}
		</Text>
	</div>
);

/**
 * Format a message template with count and plural placeholders
 */
const formatMessage = (template: string, count: number): string => {
	return template.replace('{count}', String(count)).replace('{plural}', count !== 1 ? 's' : '');
};

/**
 * Generic modal for confirming actions on selected items
 * Shows categorized items and handles confirmation flow
 * After confirmation, shows immediate success message (fire-and-forget)
 */
export const ActionConfirmationModal = ({
	isOpen,
	onOpenChange,
	categories,
	totalSelected,
	readyCount,
	onConfirm,
	onCancel,
	actionInitiatedCount,
	isConfirming = false,
	title = 'Confirm action',
	subtitle = 'You selected {count} item{plural}',
	successTitle = 'Action started',
	successIcon = <RocketLaunchIcon />,
	successMessage = '{count} job{plural} started',
	successDescription = 'Updates are processing in the background.',
	confirmButtonText = 'Confirm {count}',
	noItemsMessage = 'No items are currently available.',
}: ActionConfirmationModalProps) => {
	const hasActionStarted = actionInitiatedCount > 0;

	const handleCancel = () => {
		onCancel();
		onOpenChange(false);
	};

	const handleConfirm = () => {
		onConfirm();
	};

	const handleDone = () => {
		onOpenChange(false);
	};

	// Success view after action has been initiated
	if (hasActionStarted) {
		return (
			<DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
				<Popup title={successTitle}>
					<Stack padding='0' itemsSpacing='24'>
						{/* Success message */}
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '12px',
								padding: '16px',
								backgroundColor: 'var(--color-background-secondary, #f5f5f5)',
								borderRadius: '8px',
							}}>
							<div
								style={{
									color: 'var(--color-positive, #22c55e)',
									display: 'flex',
									alignItems: 'center',
								}}>
								{successIcon}
							</div>
							<Text size='body'>
								<strong>{actionInitiatedCount}</strong>{' '}
								{formatMessage(
									successMessage.replace('{count}', ''),
									actionInitiatedCount,
								).trim()}
							</Text>
						</div>

						{successDescription && (
							<Text size='body' color='secondary'>
								{successDescription}
							</Text>
						)}

						{/* Done button */}
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								paddingTop: '8px',
							}}>
							<Button variant='filled' size='medium' onPress={handleDone}>
								Done
							</Button>
						</div>
					</Stack>
				</Popup>
			</DialogTrigger>
		);
	}

	// Confirmation view before action
	return (
		<DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
			<Popup title={title}>
				<Stack padding='0' itemsSpacing='24'>
					{/* Subtitle */}
					<Text size='body' color='secondary'>
						{formatMessage(subtitle, totalSelected)}
					</Text>

					{/* Status rows */}
					<Stack itemsSpacing='12'>
						{categories
							.filter((category) => category.count > 0)
							.map((category, index) => (
								<StatusRow
									key={index}
									icon={category.icon}
									iconColor={category.iconColor}
									count={category.count}
									message={category.message}
								/>
							))}

						{/* No items can proceed */}
						{readyCount === 0 && (
							<Text size='bodySmall' color='secondary'>
								{noItemsMessage}
							</Text>
						)}
					</Stack>

					{/* Action buttons */}
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: '12px',
							paddingTop: '8px',
						}}>
						<Button
							variant='secondary'
							size='medium'
							onPress={handleCancel}
							isDisabled={isConfirming}>
							Cancel
						</Button>
						<Button
							variant='filled'
							size='medium'
							onPress={handleConfirm}
							isLoading={isConfirming}
							isDisabled={readyCount === 0 || isConfirming}>
							{readyCount > 0
								? formatMessage(confirmButtonText, readyCount)
								: confirmButtonText.replace('{count}', '').trim()}
						</Button>
					</div>
				</Stack>
			</Popup>
		</DialogTrigger>
	);
};
